import { isDefined, isEmpty } from '@sgme/fp';
import type { FormError } from 'models/formError';
import type { TradeConfirmationBackToBackFields } from 'state/uiPanelState/tradeConfirmation/tradeConfirm.model';

export function isValueOutOfRange(value: number, min: number, max: number): boolean {
  return value < min || value > max;
}

export function validateBackedNotionalPercentage(backedNotional: {
  value?: number;
  unit: string;
}): FormError<TradeConfirmationBackToBackFields> | undefined {
  if (!isDefined(backedNotional.value)) {
    return { fields: ['backedNotional'], translationKey: 'backToBack.errors.backedNotional.percentage' };
  }

  const isInvalid = backedNotional.value < 0 || backedNotional.value > 100;

  if (isInvalid) {
    return { fields: ['backedNotional'], translationKey: 'backToBack.errors.backedNotional.percentage' };
  }

  return undefined;
}

export function validateBackedNotionalCurrency(
  backedNotional: { value?: number; unit: string },
  notional: number,
): FormError<TradeConfirmationBackToBackFields> | undefined {
  if (!isDefined(backedNotional.value)) {
    return {
      fields: ['backedNotional'],
      translationKey: 'backToBack.errors.backedNotional.currency',
      values: { unit: backedNotional.unit, notional },
    };
  }

  const isInvalid = backedNotional.value < 0 || backedNotional.value > notional;

  if (isInvalid) {
    return {
      fields: ['backedNotional'],
      translationKey: 'backToBack.errors.backedNotional.currency',
      values: { unit: backedNotional.unit, notional },
    };
  }

  return undefined;
}

export function validateClientTradeBackToBackFields(
  backedNotional: { value?: number; unit: string } | undefined,
  notional: number,
  isDelayedBackToBack?: { value?: boolean },
): FormError<TradeConfirmationBackToBackFields>[] {
  const delayedBackToBackErrors = validateIsDelayedBackToBack(isDelayedBackToBack);

  if (!backedNotional || isDelayedBackToBack?.value === true) {
    return delayedBackToBackErrors;
  }

  const error =
    backedNotional.unit === '%'
      ? validateBackedNotionalPercentage(backedNotional)
      : validateBackedNotionalCurrency(backedNotional, notional);

  return [...(error ? [error] : []), ...delayedBackToBackErrors];
}

export function validateHedgingTradeBackToBackFields(
  fileCode?: { value?: string },
  isDeltaAdjusted?: { value?: boolean },
  noteNominal?: { value?: number },
  isDelayedBackToBack?: { value?: boolean },
): FormError<TradeConfirmationBackToBackFields>[] {
  const errors: FormError<TradeConfirmationBackToBackFields>[] = [];

  if (isEmpty(fileCode?.value)) {
    errors.push({ fields: ['fileCode'], translationKey: 'backToBack.errors.fileCode' });
  }

  if (!isDefined(isDeltaAdjusted?.value)) {
    errors.push({ fields: ['isDeltaAdjusted'], translationKey: 'backToBack.errors.isDeltaAdjusted' });
  }

  if (!isDefined(noteNominal?.value) || noteNominal!.value! <= 0) {
    errors.push({ fields: ['noteNominal'], translationKey: 'backToBack.errors.noteNominal' });
  }

  errors.push(...validateIsDelayedBackToBack(isDelayedBackToBack));

  return errors;
}

export function validateIsDelayedBackToBack(isDelayedBackToBack?: {
  value?: boolean;
}): FormError<TradeConfirmationBackToBackFields>[] {
  if (!isDefined(isDelayedBackToBack?.value)) {
    return [{ fields: ['isDelayedBackToBack'], translationKey: 'backToBack.errors.isDelayedBackToBack' }];
  }

  return [];
}
